import { useMemo } from "react";
import { B, HD, PD } from "../theme";
import { Term } from "../shared/Term";
import { GhostButton, SKY } from "../shared/ui";
import { nextReturnDays, daysUntil } from "../lib/astro/returns";

/* Your days: the next five days a tradition hands you back your own birth
   moment (src/lib/astro/returns.js). One row per date, a dot in each
   tradition's accent. Untimed charts say what they're missing. */

const ACCENT = { western: "#68B8C8", vedic: "#D4A574", mayan: "#6DA88F", chinese: "#C2625A" };
const TRADITION = { "maya-sign": "mayan", tzolkin: "mayan", chinese: "chinese", janma: "vedic", lunar: "western", solar: "western" };

// Rounded to the nearest minute (the searches run to the second).
const timeOf = (at, timeZone) => new Date(Math.round(at.getTime() / 60000) * 60000).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone });

function whenLabel(today, day) {
  const n = daysUntil(today, day);
  if (n === 0) return { top: "Today", bottom: "" };
  if (n === 1) return { top: "Tomorrow", bottom: "" };
  const d = new Date(Date.UTC(day.year, day.month - 1, day.day, 12));
  const sameYear = day.year === today.year;
  return {
    top: d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }),
    bottom: d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }), timeZone: "UTC" }),
  };
}

// "12:48 AM", or with the day when it isn't today: "12:48 AM tomorrow", "Thu 12:48 AM".
function untilLabel(today, item, timeZone) {
  const n = daysUntil(today, item.endDay);
  const t = timeOf(item.end, timeZone);
  if (n === 0) return t;
  if (n === 1) return `${t} tomorrow`;
  return `${item.end.toLocaleDateString("en-US", { weekday: "short", timeZone })} ${t}`;
}

function itemText(item, timeZone, today) {
  switch (item.kind) {
    case "maya-sign":
      return { title: <>Your <Term k="daySign">day sign</Term>, {item.sign.name}</>, sub: "Comes back every 20 days" };
    case "tzolkin":
      return { title: <>{item.tone.number} {item.sign.name}, your Maya birthday</>, sub: <>Your sign and number together, once every 260 days in the <Term k="tzolkin">Tzolk'in</Term></> };
    case "chinese":
      return { title: <Term k="yearAnimalDay">A {item.animal.name} day</Term>, sub: "Your year animal's own day, every 12 days" };
    case "janma":
      return {
        title: <>Your <Term k="janmaTara">birth star</Term>, {item.nakshatra.name}</>,
        sub: item.ongoing
          ? <>The Moon is there now, until {untilLabel(today, item, timeZone)}</>
          : <>The Moon is back in it from {timeOf(item.at, timeZone)}</>,
      };
    case "lunar":
      return { title: <>Your <Term k="lunarReturn">lunar return</Term></>, sub: <>The Moon back where it was when you were born, {timeOf(item.at, timeZone)}</> };
    case "solar":
      return { title: <>Your <Term k="solarReturn">solar return</Term></>, sub: <>The Sun back where it was when you were born, {timeOf(item.at, timeZone)}</> };
    default:
      return { title: null, sub: null };
  }
}

function missingLine(skipped) {
  const kinds = new Set(skipped.map((s) => s.kind));
  if (!kinds.size) return null;
  const parts = [];
  if (kinds.has("lunar") || kinds.has("solar")) parts.push("your lunar and solar returns");
  if (kinds.has("janma")) parts.push("your birth star (the Moon changed stars that day, so we can't tell which is yours)");
  const needs = skipped.some((s) => s.needs === "place") ? "place" : "time";
  return `Without a birth ${needs} we've left out ${parts.join(", and ")}.`;
}

export function YourDays({ birthData, date, timeZone, onEditBirth }) {
  const days = useMemo(() => {
    try { return nextReturnDays(birthData, { date: date || new Date(), timeZone }); }
    catch (e) { console.error("YourDays:", e); return null; }
  }, [birthData, date, timeZone]);
  if (!days?.rows?.length) return null;
  const missing = missingLine(days.skipped);

  return (
    <section aria-labelledby="your-days" style={{ marginTop: 30 }}>
      <h2 id="your-days" style={{ ...HD, fontSize: 20, fontWeight: 500, color: PD.text, margin: "0 0 2px" }}>Your days</h2>
      <p style={{ ...B, fontSize: 13, color: PD.sub, margin: "0 0 10px", lineHeight: 1.45 }}>When each tradition gives your birth moment back.</p>
      <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {days.rows.map((row) => {
          const when = whenLabel(days.today, row.day);
          return (
            <li key={`${row.day.year}-${row.day.month}-${row.day.day}`}
              style={{ display: "flex", gap: 14, padding: "12px 0", borderTop: `1px solid ${PD.border}` }}>
              <div style={{ width: 62, flexShrink: 0 }}>
                <p style={{ ...B, fontSize: 13, fontWeight: 600, color: PD.text, margin: 0 }}>{when.top}</p>
                {when.bottom && <p style={{ ...B, fontSize: 12, color: PD.sub, margin: "2px 0 0" }}>{when.bottom}</p>}
              </div>
              <div style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                {row.items.map((item) => {
                  const t = itemText(item, days.timeZone, days.today);
                  const accent = ACCENT[TRADITION[item.kind]];
                  return (
                    <div key={item.kind} style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
                      <span aria-hidden style={{ width: 8, height: 8, borderRadius: 4, background: accent, flexShrink: 0, transform: "translateY(-1px)" }} />
                      <div style={{ minWidth: 0 }}>
                        <p style={{ ...B, fontSize: 15, fontWeight: 500, color: PD.text, margin: 0, lineHeight: 1.35 }}>{t.title}</p>
                        <p style={{ ...B, fontSize: 13, color: PD.sub, margin: "2px 0 0", lineHeight: 1.4 }}>{t.sub}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ol>
      {missing && (
        <div style={{ borderTop: `1px solid ${PD.border}`, paddingTop: 10 }}>
          <p style={{ ...B, fontSize: 13, color: PD.sub, margin: 0, lineHeight: 1.45 }}>{missing}</p>
          {onEditBirth && <GhostButton onClick={onEditBirth} style={{ padding: "8px 0", minHeight: 44, fontSize: 13, color: SKY.b }}>{/without a birth place/i.test(missing) ? "Add your birth place" : "Add your birth time"}</GhostButton>}
        </div>
      )}
    </section>
  );
}
